import { useCallback, useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import Markers from 'src/presentation/components/molecules/Markers';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import { useMapStore } from 'src/domain/stores/useMapStore';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import {
	clampOffset,
	contentSize,
	fitInside,
	scaleForWheel,
	zoomAt,
	type Offset,
} from 'src/presentation/components/molecules/panZoom';
import type { TransformableImageProps } from 'src/presentation/components/molecules/TransformableImageTypes';

/**
 * The floorplan on web.
 *
 * react-native-image-pan-zoom is a native gesture component: in a browser it lays out at
 * zero height, so the plan never mounted and the screen sat on "Kaart laden..." forever.
 * This sibling does the same job with the two gestures a browser actually has -- the
 * wheel to zoom and dragging to pan -- and keeps the markers on the plan by scaling the
 * canvas they are measured against.
 *
 * The maths lives in `panZoom.ts` so it can be tested without a DOM.
 */
export default function TransformableImage({
	pixels: pixelsProp,
	enableTransform = true,
	enableScale = true,
	enableTranslate = true,
	onLoadStart: onLoadStartProp,
	onLoad: onLoadProp,
	onLoadEnd: onLoadEndProp,
	source,
	style,
	hotspotStyle,
}: TransformableImageProps) {
	const container = useRef<View | null>(null);
	const [viewport, setViewport] = useState({ width: 0, height: 0 });
	const [intrinsic, setIntrinsic] = useState(pixelsProp ?? { width: 0, height: 0 });
	const [loaded, setLoaded] = useState(false);
	const [failed, setFailed] = useState(false);
	const [scale, setScale] = useState(1);
	const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });

	const updateMapScale = useMapStore.getState().updateMapScale;

	// The pointer and wheel listeners attach once and read from here. Putting the
	// geometry in their dependency array instead would tear them down on the first
	// move -- `fit` is a fresh object every render, so the cleanup ran mid-gesture
	// and the plan moved exactly one step per press.
	const geometry = useRef({ fit: { width: 0, height: 0 }, scale: 1, viewport: { width: 0, height: 0 }, maxScale: 1 });
	const offsetRef = useRef<Offset>({ x: 0, y: 0 });
	const dragging = useRef(false);
	const lastPointer = useRef({ x: 0, y: 0 });

	// The plan's own pixel size decides how far you may zoom in: past its own resolution
	// there is nothing more to see.
	const fit = fitInside(intrinsic, viewport);
	const maxScale = fit.width > 0 ? Math.max(1, intrinsic.width / fit.width) : 1;
	const content = contentSize(fit, scale);

	geometry.current = { fit, scale, viewport, maxScale };
	offsetRef.current = offset;

	useEffect(() => {
		if (pixelsProp) return;
		if (!source?.uri) return;

		Image.getSize(
			source.uri,
			(width, height) => setIntrinsic({ width, height }),
			() => setFailed(true),
		);
	}, [pixelsProp, source?.uri]);

	// Re-centre whenever the frame or the zoom changes, so the plan never ends up in a
	// corner after a resize. Keyed on the measurements rather than on `fit` and `viewport`
	// themselves: both are fresh objects every render, so the effect would never stop.
	useEffect(() => {
		setOffset((current) => clampOffset(current, contentSize(fit, scale), viewport));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fit.width, fit.height, scale, viewport.width, viewport.height]);

	useEffect(() => {
		updateMapScale(scale);
	}, [scale, updateMapScale]);

	const onLayout = (event: LayoutChangeEvent) => {
		const { width, height } = event.nativeEvent.layout;
		setViewport((current) => (current.width === width && current.height === height ? current : { width, height }));
	};

	const canZoom = enableTransform && enableScale;
	const canPan = enableTransform && enableTranslate;

	const zoom = useCallback((deltaY: number, pointer: Offset) => {
		const { fit: current, scale: currentScale, viewport: frame, maxScale: limit } = geometry.current;
		const next = scaleForWheel(deltaY, currentScale, limit);
		if (next === currentScale) return;

		const zoomed = zoomAt(pointer, { scale: currentScale, offset: offsetRef.current }, next);

		setScale(next);
		setOffset(clampOffset(zoomed.offset, contentSize(current, next), frame));
	}, []);

	// Listens on the DOM node rather than through props: a wheel needs a non-passive
	// listener to be able to stop the page from scrolling behind the plan.
	useEffect(() => {
		const node = container.current as unknown as HTMLElement | null;
		if (!node || !canZoom) return;

		const onWheel = (event: WheelEvent) => {
			const box = node.getBoundingClientRect();
			zoom(event.deltaY, { x: event.clientX - box.left, y: event.clientY - box.top });
			event.preventDefault();
		};

		node.addEventListener('wheel', onWheel, { passive: false });

		return () => node.removeEventListener('wheel', onWheel);
	}, [canZoom, zoom]);

	useEffect(() => {
		const node = container.current as unknown as HTMLElement | null;
		if (!node || !canPan) return;

		const onDown = (event: PointerEvent) => {
			dragging.current = true;
			lastPointer.current = { x: event.clientX, y: event.clientY };
			node.setPointerCapture(event.pointerId);
		};

		const onMove = (event: PointerEvent) => {
			if (!dragging.current) return;

			const { fit: current, scale: currentScale, viewport: frame } = geometry.current;
			const moved = { x: event.clientX - lastPointer.current.x, y: event.clientY - lastPointer.current.y };
			lastPointer.current = { x: event.clientX, y: event.clientY };

			setOffset(
				clampOffset(
					{ x: offsetRef.current.x + moved.x, y: offsetRef.current.y + moved.y },
					contentSize(current, currentScale),
					frame,
				),
			);
		};

		const onUp = (event: PointerEvent) => {
			dragging.current = false;
			if (node.hasPointerCapture(event.pointerId)) node.releasePointerCapture(event.pointerId);
		};

		node.addEventListener('pointerdown', onDown);
		node.addEventListener('pointermove', onMove);
		node.addEventListener('pointerup', onUp);
		node.addEventListener('pointercancel', onUp);

		return () => {
			node.removeEventListener('pointerdown', onDown);
			node.removeEventListener('pointermove', onMove);
			node.removeEventListener('pointerup', onUp);
			node.removeEventListener('pointercancel', onUp);
		};
	}, [canPan]);

	if (failed) {
		return (
			<View style={style} onLayout={onLayout}>
				<EmptyState icon="map-outline" message={Translations.textNoMapMessage} title={Translations.textNoMapTitle} />
			</View>
		);
	}

	const measured = viewport.width > 0 && viewport.height > 0 && fit.width > 0 && fit.height > 0;

	return (
		<View ref={container} style={[style, styles.frame]} onLayout={onLayout}>
			{measured ? (
				<View style={[styles.canvas, { width: content.width, height: content.height, left: offset.x, top: offset.y }]}>
					<Image
						resizeMode="contain"
						source={source}
						style={styles.plan}
						onError={() => setFailed(true)}
						onLoad={() => {
							setLoaded(true);
							onLoadProp?.();
							onLoadEndProp?.();
						}}
						onLoadStart={() => {
							setLoaded(false);
							onLoadStartProp?.();
						}}
					/>
					{loaded ? (
						<Markers
							canvasHeight={content.height}
							canvasWidth={content.width}
							color={hotspotStyle ?? Colors.primary}
							pixels={intrinsic}
						/>
					) : null}
				</View>
			) : null}
			{!loaded ? (
				<View style={StyleSheet.absoluteFill}>
					<Loader message="Kaart laden..." />
				</View>
			) : null}
		</View>
	);
}

const styles = StyleSheet.create({
	frame: {
		overflow: 'hidden',
	},
	canvas: {
		position: 'absolute',
	},
	plan: {
		width: '100%',
		height: '100%',
	},
});
